import { Injectable, signal } from '@angular/core';

const STORAGE_KEY = 'chroniq_admin_tenant_override';

/**
 * Admin tenant-impersonation (Settings → Mandanten): lets an Admin who belongs to more than
 * one tenant switch which one they're currently managing, without a second Auth0 login. Backed
 * by the `X-Admin-TenantId` header that both TenantAuthMiddleware and SignalRAuthMiddleware
 * already honour server-side — they re-verify tenant membership on every request, so this is a
 * UI convenience, not a trust boundary.
 *
 * Persisted to localStorage. switchTo() always reloads the page — the only safe way to flush
 * every EntityStore's cached data, none of which is tenant-switch-aware. A switch in another
 * tab (storage event) reloads this tab too, so no tab keeps working in a tenant the next
 * reload would silently replace.
 */
@Injectable({ providedIn: 'root' })
export class TenantOverrideService {
  readonly activeTenantId = signal<string | null>(localStorage.getItem(STORAGE_KEY));

  constructor() {
    window.addEventListener('storage', (event) => {
      // key === null: storage was cleared as a whole.
      if ((event.key === STORAGE_KEY || event.key === null) && event.newValue !== this.activeTenantId()) {
        window.location.reload();
      }
    });
  }

  /** Switches the working tenant, or with `null` reverts to the admin's own default tenant. */
  switchTo(tenantId: string | null): void {
    if (tenantId) {localStorage.setItem(STORAGE_KEY, tenantId);}
    else {localStorage.removeItem(STORAGE_KEY);}
    window.location.reload();
  }

  /** Called on logout, without a reload (the Auth0 logout redirect handles that) — a stale
   *  override must never leak into a different account's next session. */
  clearSilently(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.activeTenantId.set(null);
  }

  /**
   * Drops an override the server did not honour (deleted tenant, membership gone, another
   * account): the data is already the default tenant's, so no reload is needed, only the
   * UI claim and the header have to go.
   */
  dropIfIgnored(servedTenantId: string | null): void {
    const active = this.activeTenantId();
    if (active && servedTenantId !== active) {
      this.clearSilently();
    }
  }
}
